export type PhotoAdjustmentName = 'brightness' | 'contrast' | 'temperature' | 'saturation';

export interface PhotoAdjustments {
  brightness: number;
  contrast: number;
  temperature: number;
  saturation: number;
}

export const PHOTO_ADJUSTMENT_LIMITS = {
  brightness: { min: -25, max: 25, step: 1 },
  contrast: { min: -25, max: 25, step: 1 },
  temperature: { min: -20, max: 20, step: 1 },
  saturation: { min: -25, max: 25, step: 1 },
} as const;

export const DEFAULT_PHOTO_ADJUSTMENTS: PhotoAdjustments = {
  brightness: 0,
  contrast: 0,
  temperature: 0,
  saturation: 0,
};

export function resetPhotoAdjustments(): PhotoAdjustments {
  return { ...DEFAULT_PHOTO_ADJUSTMENTS };
}

function clampChannel(value: number): number {
  return Math.max(0, Math.min(255, Math.round(value)));
}

/**
 * Applies the local enhancement recipe in-place to RGBA pixels.
 * A neutral recipe returns without touching the bytes, which is important for
 * the lossless reset/neutral export path.
 */
export function applyPhotoAdjustmentsToRgba(
  pixels: Uint8Array | Uint8ClampedArray,
  adjustments: PhotoAdjustments,
): Uint8Array | Uint8ClampedArray {
  if (adjustments.brightness === 0
    && adjustments.contrast === 0
    && adjustments.temperature === 0
    && adjustments.saturation === 0) {
    return pixels;
  }

  const brightnessOffset = adjustments.brightness * 2.55;
  const contrastFactor = (259 * (adjustments.contrast + 255))
    / (255 * (259 - adjustments.contrast));
  const temperatureScale = adjustments.temperature / PHOTO_ADJUSTMENT_LIMITS.temperature.max;
  const saturationFactor = 1 + adjustments.saturation / 100;

  for (let offset = 0; offset < pixels.length; offset += 4) {
    let red = pixels[offset] + brightnessOffset;
    let green = pixels[offset + 1] + brightnessOffset;
    let blue = pixels[offset + 2] + brightnessOffset;

    red = (red - 128) * contrastFactor + 128;
    green = (green - 128) * contrastFactor + 128;
    blue = (blue - 128) * contrastFactor + 128;

    // A small red/blue shift gives a soft temperature correction without
    // changing geometry, adding detail, or producing a stylised colour cast.
    red += temperatureScale * 12;
    green += temperatureScale * 3;
    blue -= temperatureScale * 12;

    const luminance = red * 0.2126 + green * 0.7152 + blue * 0.0722;
    red = luminance + (red - luminance) * saturationFactor;
    green = luminance + (green - luminance) * saturationFactor;
    blue = luminance + (blue - luminance) * saturationFactor;

    pixels[offset] = clampChannel(red);
    pixels[offset + 1] = clampChannel(green);
    pixels[offset + 2] = clampChannel(blue);
    // Alpha is intentionally untouched.
  }

  return pixels;
}
